import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';

import sendRequest from '../../../../service/sendRequest';
import callApi from '../../../../service/callApi';
import { useProject } from '../../../../context/ProjectContext';
import { PROJECT_VARIANTS } from '../../../../constants';

const useBenchmarkValue = ({ benchmarkNodes }) => {
  const { id: projectId } = useParams();
  const { projectVariant } = useProject();

  const [benchmarkValue, setBenchmarkValue] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef(null);

  const { projectName, projectPassword } = useSelector((state) => state.consumer.resonance);

  const requestData = {
    [PROJECT_VARIANTS.ADMIN]: {
      url: `/api/resonance/calculations/benchmarks?projectId=${projectId}`,
      data: benchmarkNodes.map((node) => node.id),
    },
    [PROJECT_VARIANTS.CONSUMER]: {
      url: `/api/consumer/resonance/calculations/benchmarks`,
      data: {
        projectRequest: {
          projectName,
          projectPassword,
        },
        request: benchmarkNodes.map((node) => node.id),
      },
    },
  };

  const loadBenchmarkValue = useCallback(async () => {
    if (!benchmarkNodes.length) {
      setBenchmarkValue(null);
      setIsLoading(false);

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsLoading(true);

    try {
      const response = await sendRequest(
        () =>
          callApi.post(requestData[projectVariant].url, requestData[projectVariant].data, {
            signal: abortController.signal,
          }),
        {
          onError: (error) => {
            if (error.name !== 'AbortError') {
              setBenchmarkValue(null);
            }
          },
        },
      );

      if (!abortController.signal.aborted) {
        setBenchmarkValue(`${response.data}%`);
      }
    } finally {
      if (!abortController.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [benchmarkNodes, projectId]);

  useEffect(() => {
    loadBenchmarkValue();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [benchmarkNodes, loadBenchmarkValue]);

  return { benchmarkValue, isLoading };
};

export default useBenchmarkValue;
